<template>
	<BuyersOrderGroup
		:name="bucketType"
		:hideExtraColumns="hideProfits"
		:isOpenByDefault
	>
		<template #group-action>
			<span
				v-if="addNewItemPrompt"
				class="new-tax-line ignore-deal-lock"
			>
				<button
					class="button button-edit"
					@click="onAddEditItemClick()"
					:disabled="costLockCondition()"
				>
					<i class="fas fa-plus"></i> {{ addNewItemPrompt }}
				</button>
			</span>
		</template>

		<template #right-icon>
			<div
				v-if="!areBucketsValid"
				class="error-text bo-bucket-error"
			>
				<TooltipComponent
					v-if="bucketValidationMessage"
					:tooltip="[bucketValidationMessage]"
					direction="top"
					isError
				>
					<template #showFromOutside>
						<i class="fas fa-exclamation-circle"></i>
					</template>
				</TooltipComponent>
				<i
					v-else
					class="fas fa-exclamation-circle"
				></i>
			</div>
			<span v-if="sign == 1">+</span>
			<span v-else-if="sign == -1">-</span>
			<span v-else>+</span>
		</template>

		<template #column-1>
			<InputCurrency
				v-if="bucketType !== 'Adjustments'"
				:saturn="bucketTypeSubtotalABS"
				:disabled="true"
				:precision="2"
				iconSide="left"
				:negative="true"
				:invalid="!areBucketsValid"
			>
			</InputCurrency>
		</template>

		<template #column-2>
			<InputCurrency
				v-if="bucketTypeCost != null || showCostAndProfitEvenIfNull"
				:saturn="bucketTypeCost"
				:disabled="true"
				:precision="2"
				iconSide="left"
				:negative="true"
				:invalid="!areBucketsValid"
			>
				<template #validation>
					<div v-if="!areBucketsValid">
						{{ bucketValidationMessage ?? 'Actual Cash Value is Required' }}
					</div>
				</template>
			</InputCurrency>
		</template>

		<template #column-3>
			<InputCurrency
				v-if="bucketTypeProfit != null || showCostAndProfitEvenIfNull"
				:saturn="bucketTypeProfit"
				:disabled="true"
				:precision="2"
				iconSide="left"
				:negative="true"
			>
			</InputCurrency>
		</template>

		<template #children>
			<template
				v-for="bucketContent in bucketContents"
				:key="bucketContent.bucketItem.name"
			>
				<template v-if="getSlotName(bucketContent.bucketItem.name)">
					<slot :name="getSlotName(bucketContent.bucketItem.name)"></slot>
				</template>
				<BuyersOrderBucketItem
					:bo
					:fimenu
					:bucketContent
					:costLockCondition
					:itemValidationMessage="getItemValidationMessage(bucketContent.bucketItem)"
					:fimenuValidation
					:hideProfits
					:hideSubTotal
					:sourceData
					@blur="(isInvalid: boolean) => emit('blur', isInvalid)"
					@change="emit('change')"
					@itemClick="onAddEditItemClick(bucketContent)"
				></BuyersOrderBucketItem>
			</template>
		</template>
	</BuyersOrderGroup>
</template>

<script lang="ts">
	export interface IBuyersOrderBucketProps {
		bo: FIMenuBuyersOrder;
		fimenu: FIMenu;
		fimenuValidation?: any;
		bucketType: string;
		hideSubTotal?: boolean;
		addNewItemPrompt?: string;
		hideProfits?: boolean;
		costLockCondition?: () => boolean;
		slotContainerProperties?: string[];
		sourceData?: any[];
	}
</script>

<script setup lang="ts">
	import { computed, ref } from 'vue';
	import BuyersOrderBucketItem from '@/components/buyers-order/BuyersOrderBucketItem.vue';
	import BuyersOrderGroup from '@/components/buyers-order/BuyersOrderGroup.vue';
	import FIMenu from '@core/classes/FIMenu';
	import FIMenuBuyersOrder from '@core/classes/FIMenuBuyersOrder';
	import FIMenuBuyersOrderBucket from '@core/classes/FIMenuBuyersOrderBucket';
	import FIMenuBuyersOrderBucketItem from '@core/classes/FIMenuBuyersOrderBucketItem';
	import { IBucketContent } from '@core/_interfaces/SharedInterfaces';
	import InputCurrency from '@core/components/InputCurrency.vue';
	import TooltipComponent from '@core/components/TooltipComponent.vue';
	import util from '@core/services/util';

	const {
		bo,
		fimenu,
		fimenuValidation,
		bucketType,
		hideSubTotal = false,
		addNewItemPrompt,
		hideProfits = false,
		costLockCondition = () => false,
		slotContainerProperties,
		sourceData,
	} = defineProps<IBuyersOrderBucketProps>();

	const emit = defineEmits<{
		addEditItemClick: [bucketContent: IBucketContent];
		blur: [isInvalid: boolean];
		change: [];
	}>();

	const isOpenByDefault = ref(false);

	const bucketTypeSubtotal = computed<number>(() => {
		return buckets.value.map((b: FIMenuBuyersOrderBucket) => b.bucketTotal).reduce((a: number, b: number) => a + b, 0);
	});

	const bucketTypeSubtotalABS = computed(() => {
		return Math.abs(bucketTypeSubtotal.value);
	});

	const bucketTypeCost = computed<number>(() => {
		if (buckets.value.every((b: FIMenuBuyersOrderBucket) => b.cost == null)) return null;

		return buckets.value.map((b: FIMenuBuyersOrderBucket) => b.cost).reduce((a: number, b: number) => a + b, 0);
	});

	const bucketTypeProfit = computed<number>(() => {
		if (buckets.value.every((b: FIMenuBuyersOrderBucket) => b.profit == null)) return null;

		return buckets.value.map((b: FIMenuBuyersOrderBucket) => b.profit).reduce((a: number, b: number) => a + b, 0);
	});

	const showCostAndProfitEvenIfNull = computed(() => {
		return buckets.value.some((b: FIMenuBuyersOrderBucket) => b.items.some((i: FIMenuBuyersOrderBucketItem) => i.showCostAndProfitEvenIfNull == true));
	});

	const sign = computed<0 | 1 | -1>(() => {
		if (bucketTypeSubtotal.value > 0) return 1;
		else if (bucketTypeSubtotal.value < 0) return -1;
		else return 0;
	});

	const buckets = computed<FIMenuBuyersOrderBucket[]>(() => {
		return bo.getBuckets(bucketType);
	});

	const areBucketsValid = computed<boolean>(() => {
		return bo.areBucketsValid(buckets.value, fimenuValidation);
	});

	const bucketContents = computed(() => {
		return buckets.value
			.flatMap<IBucketContent>(parentBucket => parentBucket.items.map(bucketItem => ({ parentBucket, bucketItem })))
			.sort(({ bucketItem: a }, { bucketItem: b }) => {
				// First, if lease, sort by upfront before capped
				if (bo.isLease()) {
					const isFrontComparison = Number(b.isFront) - Number(a.isFront);
					if (isFrontComparison !== 0) return isFrontComparison;
				}

				// Second, sort by rollup title (null/undefined/empty titles go last)
				const rollupTitleA: string = a.metadata?.rollupTitle ?? '';
				const rollupTitleB: string = b.metadata?.rollupTitle ?? '';

				if (rollupTitleA || rollupTitleB) {
					if (!rollupTitleA) return 1;
					if (!rollupTitleB) return -1;

					const rollupTitleComparison = rollupTitleA.localeCompare(rollupTitleB);
					if (rollupTitleComparison !== 0) return rollupTitleComparison;
				}

				// Last, sort by order in metadata (if it exists)
				if (util.hasValue(a.metadata) && util.hasValue(a.metadata.order) && util.hasValue(b.metadata) && util.hasValue(b.metadata.order)) {
					if (a.metadata.order > b.metadata.order) return 1;
					else return -1;
				}

				return 0;
			});
	});

	const bucketValidationMessage = computed(() => {
		if (bucketContents.value.length === 0 || !fimenuValidation) return null;

		const messages = bucketContents.value.map(({ bucketItem: i }) => getItemValidationMessage(i)).filter(message => util.hasValue(message));
		const uniqueMessages = [...new Set(messages)];

		return uniqueMessages.length > 0 ? uniqueMessages.join(', ') : null;
	});

	function getItemValidationMessage(item: FIMenuBuyersOrderBucketItem) {
		const itemValidation = item.getCustomValidationObj(bucketType, fimenuValidation);

		if (!itemValidation) return null;

		const errors: string[] = itemValidation.$silentErrors.map((error: any) => error.$message).filter((message: string) => util.hasValue(message));
		const uniqueErrors = [...new Set(errors)];

		return uniqueErrors.length > 0 ? uniqueErrors.join(', ') : null;
	}

	function getSlotName(itemName: string) {
		const bucket = buckets.value.find((bucket: FIMenuBuyersOrderBucket) => bucket.items.some((item: FIMenuBuyersOrderBucketItem) => item.name === itemName));
		if (!bucket || !slotContainerProperties?.includes(bucket.name)) return null;

		return bucket.name;
	}

	function onAddEditItemClick(
		bucketContent: IBucketContent = {
			parentBucket: new FIMenuBuyersOrderBucket({ bucketType }),
			bucketItem: null,
		},
	) {
		emit('addEditItemClick', bucketContent);
	}

	// This used to be done on created. Since it references a computed it should be down here.
	if (hideSubTotal || !areBucketsValid.value) isOpenByDefault.value = true;
</script>

<style>
	.preload-amt {
		padding-right: 10px;
	}

	.fa-stack.fa-wrapper {
		position: relative;
	}

	span.buyers-order-upfront-capped-icon {
		font-size: 19px;
		display: inline-block;
	}

	.buyers-order-upfront-capped-icon:hover {
		cursor: default;
	}

	i.fa-upfront {
		font-size: 8px;
		top: 4px;
		position: absolute;
	}

	.fa-capped {
		background-color: black;
		top: 16px;
		left: -6px;
		position: absolute;
		width: 14px;
		height: 14px;
		border-radius: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.fa-capped i {
		font-size: 8px;
		color: white;
	}

	.bucket-item-name {
		width: 100%;
	}

	.bucket-item-name-container.clickable {
		color: var(--main-color);
	}

	.bucket-item-name-container {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100% !important;
	}

	.bucket-item-subname {
		position: relative;
		font-size: 13px;
		color: var(--black-60percent);
		overflow: hidden;
		width: 100%;
	}

	.bucket-item-subname .content {
		position: absolute;
		text-wrap: nowrap;
	}

	.bo-bucket-sign {
		position: relative;
	}

	.bo-bucket-error {
		position: absolute;
		right: 20px;
		display: flex;
		align-items: center;
		height: 100%;
	}
</style>
